import type { RegisteredComponent } from "@builder.io/sdk-react";
import { isEditing, isPreviewing } from "@builder.io/sdk-react";

import { DEFAULT_TILE_DISPLAY } from "~/hec/components/organisms/dynamic-product-tile/display-defaults";
import { DynamicProductTile } from "~/hec/components/organisms/dynamic-product-tile/dynamic-product-tile";
import {
  resolveTileProductId,
  withoutUndefined,
} from "~/hec/dynamic-product-tile/dynamic-product-tile.logic";

import { dynamicProductTileInputs } from "./inputs";
import type { BuilderDynamicProductTileProps } from "./types";

/**
 * POC — a single product tile an author configures in Builder. Thin wrapper:
 * resolves the chosen product id and maps toggles onto the
 * `DynamicProductTile` organism, spreading `attributes` on the root (`noWrap`
 * contract, CLAUDE.md §4). For three matching tiles use `FeaturedProductSlider`.
 */
const BuilderDynamicProductTile = ({
  productId,
  image,
  attributes,
  ...tileProps
}: BuilderDynamicProductTileProps) => {
  const { className, ...rest } = attributes ?? {};
  return (
    <DynamicProductTile
      // Defaults applied HERE (see `DEFAULT_TILE_DISPLAY`); Builder sends
      // `undefined` for a reset field, which must not beat the default.
      {...DEFAULT_TILE_DISPLAY}
      {...withoutUndefined(tileProps)}
      productId={resolveTileProductId(productId)}
      imageSrc={image}
      inEditor={isEditing() || isPreviewing()}
      className={className}
      {...rest}
    />
  );
};

export const DynamicProductTileDefinition = {
  component: BuilderDynamicProductTile,
  name: "DynamicProductTile",
  // Label only — safe to rename. `name` above is the published contract.
  friendlyName: "Product tile",
  noWrap: true,
  canHaveChildren: false,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/shopping-bag.svg",
  inputs: dynamicProductTileInputs,
} satisfies RegisteredComponent;

export default BuilderDynamicProductTile;
